import React, { Suspense, FC, useEffect, useRef, useState, useMemo } from 'react';
import { Canvas, useThree, useFrame, invalidate } from '@react-three/fiber';
import { useDanceStore, calculateGroundedElevation, INITIAL_POSE, JOINT_PARENT_MAP } from '../store/useDanceStore';
import { DanceState, Pose, JointRotation } from '../types';
import { Anchor, Trash2, MapPin, Target, Save, ArrowDown, ArrowUp, RotateCcw, ChevronDown, Compass, Footprints, Tag, Axis3d, Link, PersonStanding, Move, RefreshCcw, FlipHorizontal2, Camera, X } from 'lucide-react';
import { Line, OrbitControls, Environment, Grid, ContactShadows, Html } from '@react-three/drei';
import { ErrorBoundary } from './ErrorBoundary';
import * as THREE from 'three';
import { PrimitiveMannequin } from './PrimitiveMannequin';
import { JOINT_CONSTRAINTS, getJointRotationOrder } from '../utils/constraints';
import { useStorageStore } from '../store/useStorageStore';
import { TUTORIAL_MODULES } from '../utils/tutorialData';
import { DirectorsSofa, Audience } from './DirectorsChair';
import { MenuItem } from './ContextMenu';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';
import { trackEditorEvent } from '../lib/analytics';


// Move expensive Shape/Geometry creation outside the component to prevent re-instantiation
const AUDIENCE_SHAPE = new THREE.Shape();
AUDIENCE_SHAPE.moveTo(0, 0.8);      // Tip
AUDIENCE_SHAPE.lineTo(0.6, -0.4);   // Right Wing
AUDIENCE_SHAPE.lineTo(0, -0.1);     // Inner Notch
AUDIENCE_SHAPE.lineTo(-0.6, -0.4);  // Left Wing
AUDIENCE_SHAPE.lineTo(0, 0.8);      // Back to Tip

const AUDIENCE_GEO = new THREE.ExtrudeGeometry(AUDIENCE_SHAPE, { 
  depth: 0.15, 
  bevelEnabled: true, 
  bevelThickness: 0.05, 
  bevelSize: 0.05, 
  bevelSegments: 3 
});

const MannequinWrapper: FC = () => {
  const groupRef = useRef<THREE.Group>(null);
  const rotationGroupRef = useRef<THREE.Group>(null);
  const innerGroupRef = useRef<THREE.Group>(null);

  const activeModule = useDanceStore(s => s.activeModule);
  const contextMenu = useDanceStore(s => s.contextMenu);
  const isFeedbackOpen = useDanceStore(s => s.isFeedbackOpen);

  const tutorialProgress = useStorageStore(state => state.tutorialProgress);
  const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
  const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
  const isOrientationsTutorial = currentStep?.id === 'stage-orientations';

  // Use useFrame to update position/rotation directly from store without re-rendering.
  // Elevation is applied as a single world-space translation on the outer group,
  // outside of rotation, so it means the same thing (a fixed world point) at any
  // orientation — this is what lets updateTorsoRotation solve for it directly
  // without hitting a gimbal-style singularity at 90deg pitch.
  useFrame(() => {
    const pose = useDanceStore.getState().currentPose;
    const elevation = pose.elevation || [0, 0, 0];
    const orientation = pose.orientation || [0, 0, 0];

    if (groupRef.current) {
      groupRef.current.position.set(elevation[0], -0.05 + elevation[1], elevation[2]);
    }
    if (rotationGroupRef.current) {
      rotationGroupRef.current.rotation.set(
        THREE.MathUtils.degToRad(orientation[0] || 0),
        THREE.MathUtils.degToRad(orientation[1] || 0),
        THREE.MathUtils.degToRad(orientation[2] || 0)
      );
    }
  });

  return (
    <group ref={groupRef}>
      <group ref={rotationGroupRef}>
        <group ref={innerGroupRef} scale={0.5}>
          <PrimitiveMannequin />

          {activeModule === 'PoseDesigner' && !contextMenu?.isOpen && !isFeedbackOpen && (
            <>
              {/* Static Orbs for Left/Right distinction - Subdued appearance */}
              <mesh position={[8, 11, 0]}>
                <sphereGeometry args={[0.32, 16, 16]} />
                <meshStandardMaterial
                  color="#ef4444"
                  emissive="#ef4444"
                  emissiveIntensity={isOrientationsTutorial ? 2.4 : 0.6}
                  transparent
                  opacity={isOrientationsTutorial ? 1.0 : 0.72}
                />
              </mesh>

              <mesh position={[-8, 11, 0]}>
                <sphereGeometry args={[0.32, 16, 16]} />
                <meshStandardMaterial
                  color="#22c55e"
                  emissive="#22c55e"
                  emissiveIntensity={isOrientationsTutorial ? 3.0 : 0.6}
                  transparent
                  opacity={isOrientationsTutorial ? 1.0 : 0.72}
                />

                {isOrientationsTutorial && (
                  <Html center portal={{ current: document.body }} pointerEvents="none" style={{ pointerEvents: 'none' }}>
                    <div id="tutorial-target-green-orb" style={{ width: '60px', height: '60px', pointerEvents: 'none' }} />
                  </Html>
                )}
              </mesh>
            </>
          )}
        </group>
      </group>
    </group>
  );
};

const DynamicContactShadows: FC = () => {
  const elevationY = useDanceStore(s => (s.currentPose.elevation || [0,0,0])[1]);
  return (
    <ContactShadows
      position={[0, 0, 0]}
      opacity={0.95}
      scale={20}
      blur={1.2}
      far={10 + elevationY}
      color="#000000"
      resolution={512}
    />
  );
};

const MouseInteractionController: FC = () => {
  const { camera, scene, gl } = useThree();
  const selectedJoint = useDanceStore(s => s.selectedJoint);
  const isDirectDragging = useDanceStore(s => s.isDirectDragging);
  const keysRef = useRef<Record<string, boolean>>({});
  
  // Pending deltas to avoid too many store updates
  const pendingGlobal = useRef<{ dx: number, dy: number, dz: number, drx: number, drz: number } | null>(null);
  const pendingTorso = useRef<{ drx: number, drz: number } | null>(null);
  const pendingJoint = useRef<{ joint: string, axis: 'x'|'y'|'z', delta: number } | null>(null);

  useFrame(() => {
    const state = useDanceStore.getState();
    if (pendingGlobal.current) {
      state.updateGlobalInteraction(pendingGlobal.current);
      pendingGlobal.current = null;
    }
    if (pendingTorso.current) {
      state.updateTorsoRotation(pendingTorso.current.drx, pendingTorso.current.drz);
      pendingTorso.current = null;
    }
    if (pendingJoint.current) {
      state.updateJointRotationRelative(pendingJoint.current.joint, pendingJoint.current.axis, pendingJoint.current.delta);
      pendingJoint.current = null;
    }
  });


  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      const isFeedbackOpen = useDanceStore.getState().isFeedbackOpen;
      const target = e.target as HTMLElement;
      if (isFeedbackOpen || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      keysRef.current[e.key.toLowerCase()] = true;
    };
    const onUp = (e: KeyboardEvent) => {
      if (!e.key) return;
      keysRef.current[e.key.toLowerCase()] = false;
      
      // Flush synchronously if no poser keys are pressed
      const x = keysRef.current['x'];
      const y = keysRef.current['y'];
      const z = keysRef.current['z'];
      const a = keysRef.current['a'];
      const s = keysRef.current['s'];
      const d = keysRef.current['d'];
      const f = keysRef.current['f'];

      if (!x && !y && !z && !a && !s && !d && !f) {
        const state = useDanceStore.getState();
        if (pendingGlobal.current) {
          state.updateGlobalInteraction(pendingGlobal.current);
          pendingGlobal.current = null;
        }
        if (pendingTorso.current) {
          state.updateTorsoRotation(pendingTorso.current.drx, pendingTorso.current.drz);
          pendingTorso.current = null;
        }
        if (pendingJoint.current) {
          state.updateJointRotationRelative(pendingJoint.current.joint, pendingJoint.current.axis, pendingJoint.current.delta);
          pendingJoint.current = null;
        }
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  useEffect(() => {
    let lastX = isDirectDragging ? useDanceStore.getState().directDragStartX : 0;
    let lastY = isDirectDragging ? useDanceStore.getState().directDragStartY : 0;
    let accumulatedRotation = 0;
    let wheelTimeout: number | undefined;

    const onPointerMove = (e: PointerEvent) => {
      const state = useDanceStore.getState();

      if (state.isDirectDragging && state.directDragJoint && state.directDragParent && state.directDragStartPos) {
        const jointName = state.directDragJoint;
        const parentJointName = state.directDragParent;

        const childObj = scene.getObjectByName(jointName);
        const parentObj = scene.getObjectByName(parentJointName);

        if (!childObj || !parentObj) return;

        // 1. Construct the Drag Plane parallel to camera viewport passing through child's start position
        const startPos = new THREE.Vector3(...state.directDragStartPos);
        const planeNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(camera.quaternion).normalize();
        const dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, startPos);

        // 2. Get mouse NDC coordinates
        const canvas = gl.domElement;
        const rect = canvas.getBoundingClientRect();
        const mouseX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const mouseY = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        // 3. Intersect ray from camera with the drag plane
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), camera);
        const targetPos = new THREE.Vector3();
        if (!raycaster.ray.intersectPlane(dragPlane, targetPos)) return;

        // 4. Calculate target vector relative to parent joint position
        const parentWorldPos = new THREE.Vector3();
        parentObj.getWorldPosition(parentWorldPos);
        const targetBoneVec = new THREE.Vector3().subVectors(targetPos, parentWorldPos);

        // 5. Convert target vector to parent's local space (relative to grandparent)
        const localTargetVec = targetBoneVec.clone().normalize();
        if (parentObj.parent) {
          const grandparentWorldQuat = new THREE.Quaternion();
          parentObj.parent.getWorldQuaternion(grandparentWorldQuat);
          localTargetVec.applyQuaternion(grandparentWorldQuat.invert());
        }

        // 6. Find identity local bone direction
        const uDefault = childObj.position.clone().normalize();

        // 7. Calculate rotations and clamp to limits
        const limits = JOINT_CONSTRAINTS[parentJointName];
        const isHinge = limits && limits.y[0] === 0 && limits.y[1] === 0 && limits.z[0] === 0 && limits.z[1] === 0;

        const clampJointAngles = (name: string, x: number, y: number, z: number): [number, number, number] => {
          const l = JOINT_CONSTRAINTS[name];
          if (!l) return [x, y, z];
          const clampVal = (val: number, range: [number, number]) => Math.max(range[0], Math.min(range[1], val));
          return [
            clampVal(x, l.x),
            clampVal(y, l.y),
            clampVal(z, l.z)
          ];
        };

        let cx = 0, cy = 0, cz = 0;
        const order = getJointRotationOrder(parentJointName) as THREE.EulerOrder;
        const currentRotation = (state.currentPose[parentJointName] as JointRotation) || [0, 0, 0];

        if (isHinge) {
          // Hinge rotates only around X axis.
          // Angle is computed relative to downward default (y=-1, z=0), with inverted sign for elbows/knees.
          const angleRad = Math.atan2(localTargetVec.z, -localTargetVec.y);
          const rx = -THREE.MathUtils.radToDeg(angleRad);
          [cx, cy, cz] = clampJointAngles(parentJointName, rx, currentRotation[1], currentRotation[2]);
        } else {
          // Ball-and-socket rotates around all three axes, but we lock Y twist to the current store value.
          const qLocal = new THREE.Quaternion().setFromUnitVectors(uDefault, localTargetVec);
          const euler = new THREE.Euler().setFromQuaternion(qLocal, order);
          const rx = THREE.MathUtils.radToDeg(euler.x);
          const rz = THREE.MathUtils.radToDeg(euler.z);
          [cx, cy, cz] = clampJointAngles(parentJointName, rx, currentRotation[1], rz);
        }

        // 8. Apply directly to the store and ThreeJS object rotation
        state.setJointRotation(parentJointName, cx, cy, cz);
        parentObj.rotation.set(
          THREE.MathUtils.degToRad(cx),
          THREE.MathUtils.degToRad(cy),
          THREE.MathUtils.degToRad(cz),
          order
        );
        parentObj.updateMatrixWorld(true);

        invalidate();
        return;
      }

      if (!selectedJoint) return;

      const deltaX = e.clientX - lastX;
      const deltaY = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;

      const a = keysRef.current['a'];
      const s = keysRef.current['s'];
      const d = keysRef.current['d'];
      const f = keysRef.current['f'];

      if (f && selectedJoint === 'orientation') {
        const rSpeed = 0.5;

        if (!pendingTorso.current) {
          pendingTorso.current = { drx: 0, drz: 0 };
        }
        pendingTorso.current.drx += deltaY * rSpeed;
        pendingTorso.current.drz += -deltaX * rSpeed;

        invalidate();
        return;
      }

      if ((a || s || d) && selectedJoint === 'orientation') {
        const tSpeed = 0.05;
        const rSpeed = 0.5;

        if (!pendingGlobal.current) {
          pendingGlobal.current = { dx: 0, dy: 0, dz: 0, drx: 0, drz: 0 };
        }

        const p = pendingGlobal.current;
        if (s) {
          // Left/right drag is treated the same as up/down (issue #9): users often drag
          // horizontally when raising/lowering, so fold deltaX into the same elevation change.
          p.dy += -(deltaY + deltaX) * tSpeed;
        } else if (d) {
          p.dx += deltaX * tSpeed;
          p.dz += deltaY * tSpeed;
        } else if (a) {
          p.drx += deltaY * rSpeed;
          p.drz += -deltaX * rSpeed;
        }

        invalidate();
        return;
      }

      // Dragging right (positive deltaX) or up (negative deltaY) increases rotation
      const speed = 0.5; // Degrees per pixel
      const overallDelta = deltaX - deltaY;
      const change = overallDelta * speed;

      const x = keysRef.current['x'];
      const y = keysRef.current['y'];
      const z = keysRef.current['z'];

      const isSpine = ['torso', 'stomach', 'neck', 'head'].includes(selectedJoint);
      const axis = x ? 'x' : y ? 'y' : z ? 'z' : null;
      if (!axis) return;

      const delta = (axis === 'x' || axis === 'z') && isSpine ? -change : change;
      
      if (!pendingJoint.current) {
        pendingJoint.current = { joint: selectedJoint, axis, delta };
      } else if (pendingJoint.current.joint === selectedJoint && pendingJoint.current.axis === axis) {
        pendingJoint.current.delta += delta;
      } else {
        // Flush previous immediately if joint/axis changed
        useDanceStore.getState().updateJointRotationRelative(pendingJoint.current.joint, pendingJoint.current.axis, pendingJoint.current.delta);
        pendingJoint.current = { joint: selectedJoint, axis, delta };
      }
      
      invalidate();
    };

    const onPointerUp = () => {
      const state = useDanceStore.getState();
      if (state.isDirectDragging) {
        const joint = state.directDragJoint || 'none';
        const parent = state.directDragParent || 'none';
        state.endDirectDrag();
        trackEditorEvent('direct_drag_pose_action', {
          joint,
          parentJoint: parent
        });
      } else {
        // Synchronously flush accumulated pointer movement values
        if (pendingGlobal.current) {
          state.updateGlobalInteraction(pendingGlobal.current);
          pendingGlobal.current = null;
        }
        if (pendingTorso.current) {
          state.updateTorsoRotation(pendingTorso.current.drx, pendingTorso.current.drz);
          pendingTorso.current = null;
        }
        if (pendingJoint.current) {
          state.updateJointRotationRelative(pendingJoint.current.joint, pendingJoint.current.axis, pendingJoint.current.delta);
          pendingJoint.current = null;
        }

        // Track keyboard pose actions completed
        const pressedKeys: string[] = [];
        if (keysRef.current['x']) pressedKeys.push('x');
        if (keysRef.current['y']) pressedKeys.push('y');
        if (keysRef.current['z']) pressedKeys.push('z');
        if (keysRef.current['a']) pressedKeys.push('a');
        if (keysRef.current['s']) pressedKeys.push('s');
        if (keysRef.current['d']) pressedKeys.push('d');
        if (keysRef.current['f']) pressedKeys.push('f');
        if (pressedKeys.length > 0) {
          trackEditorEvent('keyboard_pose_action', {
            joint: selectedJoint || 'none',
            keys: pressedKeys.join('+')
          });
        }
        state.setIsPosing(false);
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!selectedJoint) return;

      const x = keysRef.current['x'];
      const y = keysRef.current['y'];
      const z = keysRef.current['z'];
      const a = keysRef.current['a'];
      const s = keysRef.current['s'];
      const d = keysRef.current['d'];
      const f = keysRef.current['f'];

      if (x || y || z || ((a || s || d || f) && selectedJoint === 'orientation')) {
        // Crucial: stop OrbitControls from reacting
        e.preventDefault();
        e.stopPropagation();

        const state = useDanceStore.getState();
        state.commitHistory('Viewport Drag');
        state.setIsPosing(true);

        lastX = e.clientX;
        lastY = e.clientY;
        accumulatedRotation = 0;
      }
    };

    const onWheel = (e: WheelEvent) => {
      if (!selectedJoint) return;

      const x = keysRef.current['x'];
      const y = keysRef.current['y'];
      const z = keysRef.current['z'];
      const a = keysRef.current['a'];
      const s = keysRef.current['s'];
      const d = keysRef.current['d'];
      const f = keysRef.current['f'];

      if (x || y || z || ((a || s || d || f) && selectedJoint === 'orientation')) {
        e.preventDefault(); // Prevent page scroll / 3D zoom
        e.stopPropagation(); // Block OrbitControls zoom

        // Coalesce history for continuous scrolling
        if (!wheelTimeout) {
          useDanceStore.getState().commitHistory('Viewport Wheel');
        }
        clearTimeout(wheelTimeout);
        wheelTimeout = window.setTimeout(() => {
          wheelTimeout = undefined;
          const pressedKeys: string[] = [];
          if (keysRef.current['x']) pressedKeys.push('x');
          if (keysRef.current['y']) pressedKeys.push('y');
          if (keysRef.current['z']) pressedKeys.push('z');
          if (keysRef.current['a']) pressedKeys.push('a');
          if (keysRef.current['s']) pressedKeys.push('s');
          if (keysRef.current['d']) pressedKeys.push('d');
          if (keysRef.current['f']) pressedKeys.push('f');
          if (pressedKeys.length > 0) {
            trackEditorEvent('keyboard_pose_action', {
              joint: selectedJoint || 'none',
              keys: pressedKeys.join('+')
            });
          }
        }, 500);

        // Typical mouse wheel gives deltaY in multiples of 100, scroll up is negative
        const change = e.deltaY > 0 ? -1 : 1;

        const state = useDanceStore.getState();

        if (s && selectedJoint === 'orientation') {
          state.updateGlobalInteraction({
            dy: change * 0.1
          });
          return;
        }

        if (d && selectedJoint === 'orientation') {
          state.updateGlobalInteraction({
            dx: change * 0.1
          });
          return;
        }

        const isSpine = ['torso', 'stomach', 'neck', 'head'].includes(selectedJoint);

        if (x && selectedJoint !== 'orientation') state.updateJointRotationRelative(selectedJoint, 'x', isSpine ? -change : change);
        if (y) state.updateJointRotationRelative(selectedJoint, 'y', change);
        if (z && selectedJoint !== 'orientation') state.updateJointRotationRelative(selectedJoint, 'z', isSpine ? -change : change);
        invalidate();
      }
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('wheel', onWheel);
      clearTimeout(wheelTimeout);
    };
  }, [selectedJoint]);

  return null;
};

const CameraSync: FC<{ controlsRef: React.RefObject<any>, isOrientationsTutorial?: boolean }> = ({ controlsRef, isOrientationsTutorial }) => {
  const { camera } = useThree();
  const controls = useThree(state => (state as any).controls);
  const setLastCameraState = useDanceStore(s => s.setLastCameraState);
  const resetCamera = useDanceStore(s => s.resetCamera);
  const { isMobile, isLandscapeMobile } = useIsMobile();
  const isInitial = useRef(true);

  const isPosing = useDanceStore(s => s.isPosing);

  const tutorialProgress = useStorageStore(state => state.tutorialProgress);
  const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
  const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
  const isTorsoCameraStep = currentStep?.id === 'adv-torso-t' || currentStep?.id === 'adv-torso-v';

  useEffect(() => {
    if (isMobile) {
      if (isInitial.current) {
        isInitial.current = false;
        return;
      }
      resetCamera();
    }
  }, [isLandscapeMobile, isMobile, resetCamera]);

  useEffect(() => {
    if (!controls) return;

    const handleSync = (isUserInteraction = false) => {
      const target = controls.target;
      setLastCameraState({
        position: [camera.position.x, camera.position.y, camera.position.z],
        quaternion: [camera.quaternion.x, camera.quaternion.y, camera.quaternion.z, camera.quaternion.w],
        target: [target.x, target.y, target.z],
        zoom: (camera as any).zoom || 1
      });
      if (isUserInteraction) {
        trackEditorEvent('camera_adjusted', {
          position: [camera.position.x.toFixed(2), camera.position.y.toFixed(2), camera.position.z.toFixed(2)],
          target: [target.x.toFixed(2), target.y.toFixed(2), target.z.toFixed(2)],
          zoom: ((camera as any).zoom || 1).toFixed(2)
        });
      }
    };

    // Sync immediately when controls become available
    handleSync(false);

    // Only sync to store when interaction ends or damping settles to keep pan/rotate silky smooth
    const handleEnd = () => handleSync(true);
    controls.addEventListener('end', handleEnd);
    return () => controls.removeEventListener('end', handleEnd);
  }, [camera, controls, setLastCameraState]);

  // Synchronously disable OrbitControls when posing starts so it can't
  // process pointer events in the gap before the React re-render fires.
  useEffect(() => {
    if (!controls) return;
    return useDanceStore.subscribe((state, prevState) => {
      if (state.isPosing !== prevState.isPosing) {
        controls.enabled = !state.isPosing;
      }
    });
  }, [controls]);

  // Handle tutorial-specific camera zoom-out
  useEffect(() => {
    if (controls && isOrientationsTutorial) {
      controls.target.set(-0.065, 3.5, 0.429);
      camera.position.set(-0.065, 12.5, 24); // Zoomed out for better view of stage indicators
      controls.update();
    }
  }, [isOrientationsTutorial, controls, camera]);

  // Set torso-specific camera angle for Advanced Posing Steps 2 & 3
  useEffect(() => {
    if (controls && isTorsoCameraStep) {
      controls.target.set(0.0, 2.6, 0.7);
      camera.position.set(16.7, 4.9, 10.9);
      controls.update();
    }
    return () => {
      if (controls && isTorsoCameraStep) {
        controls.target.set(-0.0, 2.6, 0.7);
        camera.position.set(-0.0, 12.1, 18.0);
        controls.update();
      }
    };
  }, [isTorsoCameraStep, controls, camera]);

  const resetVersion = useDanceStore(s => s.cameraResetVersion);
  useEffect(() => {
    if (controls) {
      const targetY = isOrientationsTutorial ? 3.5 : 2.6;
      const posY = isOrientationsTutorial ? 12.5 : 12.1;
      const posZ = isOrientationsTutorial ? 24 : 18.0;

      controls.target.set(-0.0, targetY, 0.7);
      camera.position.set(-0.0, posY, posZ);
      controls.update();
    }
  }, [resetVersion, controls, camera, isOrientationsTutorial]);

  return null;
};

const FootMarkers: FC = () => {
  const { t } = useTranslation();
  const markers = useDanceStore(s => s.footMarkers);
  const removeFootMarker = useDanceStore(s => s.removeFootMarker);
  const selectedLabel = useDanceStore(s => s.selectedFootMarkerLabel);
  const setSelectedLabel = useDanceStore(s => s.setSelectedFootMarkerLabel);
  const setSelectedLocationLabel = useDanceStore(s => s.setSelectedLocationMarkerLabel);
  const [hoveredLabel, setHoveredLabel] = useState<number | null>(null);
  const [selectedSide, setSelectedSide] = useState<'left' | 'right'>('left');
  const controlsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedLabel === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (controlsRef.current?.contains(target)) return;
      if (target.closest('[data-foot-marker-hitbox]')) return;
      setSelectedLabel(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [selectedLabel]);

  if (markers.length === 0) return null;

  return (
    <group>
      {markers.map((marker, idx) => {
        const isSelected = selectedLabel === marker.label;
        return (
          <group
            key={idx}
            position={marker.position}
            rotation={[0, marker.rotation, 0]}
          >
            {/* Capsule — visual only, no raycasting */}
            <mesh rotation={[Math.PI / 2, 0, 0]} raycast={() => null}>
              <capsuleGeometry args={[0.15, 0.4, 4, 16]} />
              <meshStandardMaterial
                color={(isSelected || hoveredLabel === marker.label) ? "#7dd3fc" : "#38bdf8"}
                transparent
                opacity={(isSelected || hoveredLabel === marker.label) ? 0.8 : 0.4}
                emissive={(isSelected || hoveredLabel === marker.label) ? "#7dd3fc" : "#38bdf8"}
                emissiveIntensity={(isSelected || hoveredLabel === marker.label) ? 1.0 : 0.5}
              />
            </mesh>

            {/* Screen-space circular hitbox */}
            <Html position={[0, 0.2, 0]} style={{ pointerEvents: 'none' }}>
              <div
                data-foot-marker-hitbox
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelectedLabel(marker.label);
                  setSelectedSide(marker.side);
                  setSelectedLocationLabel(null);
                }}
                onMouseEnter={() => {
                  setHoveredLabel(marker.label);
                  const el = document.getElementById('stage-3d-container');
                  if (el) el.style.cursor = 'pointer';
                }}
                onMouseLeave={() => {
                  setHoveredLabel(null);
                  const el = document.getElementById('stage-3d-container');
                  if (el) el.style.cursor = '';
                }}
                style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  background: 'transparent',
                }}
              />
            </Html>

            {/* Label and controls */}
            <Html position={[0, 0.4, 0]} style={{ pointerEvents: 'none' }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                transform: 'translate(-50%, -100%)',
                pointerEvents: 'none'
              }}>
                <div style={{
                  background: isSelected ? '#7dd3fc' : (hoveredLabel === marker.label ? 'rgba(56, 189, 248, 1.0)' : 'rgba(56, 189, 248, 0.9)'),
                  color: '#0f172a',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  userSelect: 'none',
                  boxShadow: isSelected ? '0 0 15px rgba(125, 211, 252, 0.8)' : (hoveredLabel === marker.label ? '0 0 14px rgba(56, 189, 248, 0.9)' : '0 0 10px rgba(56, 189, 248, 0.5)'),
                  transform: hoveredLabel === marker.label && !isSelected ? 'scale(1.1)' : 'scale(1)',
                  pointerEvents: 'none',
                  transition: 'all 0.15s ease'
                }}>
                  F{marker.label}
                </div>
                {isSelected && marker.side === selectedSide && (
                  <div ref={controlsRef} style={{
                    display: 'flex',
                    gap: '6px',
                    pointerEvents: 'auto',
                    background: 'rgba(15, 23, 42, 0.85)',
                    backdropFilter: 'blur(8px)',
                    padding: '3px 6px',
                    borderRadius: '14px',
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4), 0 0 6px rgba(56, 189, 248, 0.1)',
                    marginTop: '3px',
                    animation: 'scaleIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.255)'
                  }}>
                    <button
                      id="tutorial-snap-foot-marker"
                      onClick={(e) => {
                        e.stopPropagation();
                        useDanceStore.getState().snapToFootMarkerLabel(marker.label);
                      }}
                      style={{
                        background: 'rgba(14, 165, 233, 0.2)',
                        color: '#0ea5e9',
                        border: '1px solid rgba(14, 165, 233, 0.4)',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        transition: 'all 0.2s ease',
                      }}
                      title={t('stage.snap_to_marker')}
                    >
                      <Anchor size={12} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFootMarker(marker.label);
                      }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.15)',
                        color: '#f87171',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        transition: 'all 0.2s ease',
                      }}
                      title={t('stage.delete_marker')}
                    >
                      <Trash2 size={12} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLabel(null);
                      }}
                      style={{
                        background: 'rgba(100, 116, 139, 0.15)',
                        color: '#94a3b8',
                        border: '1px solid rgba(100, 116, 139, 0.3)',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        transition: 'all 0.2s ease',
                      }}
                      title={t('common.cancel', 'Dismiss')}
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                {isSelected && (
                  <div style={{
                    width: 0,
                    height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: '6px solid rgba(56, 189, 248, 0.4)',
                  }} />
                )}
              </div>
            </Html>
          </group>
        );
      })}
    </group>
  );
};

// A click is only treated as "click on blank stage" (deselect) if the pointer
// didn't move much between down and up. Beyond this, it's the end of a drag
// gesture (e.g. rotating/moving the selected joint) and must not deselect.
const CLICK_DRAG_THRESHOLD_PX = 5;

const FloorLocationPicker: FC = () => {
  const addLocationMarkerAtPosition = useDanceStore(s => s.addLocationMarkerAtPosition);
  const isLPressed = useRef(false);
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key?.toLowerCase() === 'l') isLPressed.current = true;
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key?.toLowerCase() === 'l') isLPressed.current = false;
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  const handlePointerDown = (e: any) => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleClick = (e: any) => {
    const downPos = pointerDownPos.current;
    pointerDownPos.current = null;
    const wasDrag = !!downPos &&
      (Math.abs(e.clientX - downPos.x) > CLICK_DRAG_THRESHOLD_PX ||
        Math.abs(e.clientY - downPos.y) > CLICK_DRAG_THRESHOLD_PX);

    if (!isLPressed.current) {
      // Only deselect on a genuine click, not on the mouse-up ending a drag
      // (e.g. rotating/moving the selected joint via the keyboard+mouse gesture).
      if (wasDrag) return;
      useDanceStore.getState().setSelectedJoint(null);
      useDanceStore.getState().setSelectedFootMarkerLabel(null);
      useDanceStore.getState().setSelectedLocationMarkerLabel(null);
      return;
    }
    e.stopPropagation();
    addLocationMarkerAtPosition([e.point.x, 0.01, e.point.z]);
  };

  return (
    <mesh
      data-testid="floor-location-picker"
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.003, 0]}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <planeGeometry args={[200, 200]} />
      <meshBasicMaterial visible={false} side={THREE.DoubleSide} />
    </mesh>
  );
};

const TutorialShortcutTarget: FC = () => {
  const tutorialProgress = useStorageStore(state => state.tutorialProgress);
  const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
  const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
  const isTargetVisible = tutorialProgress.currentModuleId === 'locations-markers' && currentStep?.id === 'mark-location-shortcut';

  if (!isTargetVisible) return null;

  return (
    <group position={[-4.5, 0.01, -2.0]}>
      {/* Visual ring target on the floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <ringGeometry args={[0.3, 0.4, 32]} />
        <meshBasicMaterial color="#38bdf8" transparent opacity={0.6} />
      </mesh>
      {/* Visual center dot */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]} raycast={() => null}>
        <circleGeometry args={[0.08, 16]} />
        <meshBasicMaterial color="#38bdf8" transparent opacity={0.9} />
      </mesh>
      {/* Screen-space anchor for TutorialOverlay spotlight and line pointer */}
      <Html center portal={{ current: document.body }} pointerEvents="none" style={{ pointerEvents: 'none' }}>
        <div id="tutorial-location-click-target" style={{ width: '40px', height: '40px', pointerEvents: 'none' }} />
      </Html>
    </group>
  );
};

const LocationMarkers: FC = () => {
  const { t } = useTranslation();
  const markers = useDanceStore(s => s.locationMarkers);
  const removeLocationMarker = useDanceStore(s => s.removeLocationMarker);
  const selectedLabel = useDanceStore(s => s.selectedLocationMarkerLabel);
  const setSelectedLabel = useDanceStore(s => s.setSelectedLocationMarkerLabel);
  const setSelectedFootLabel = useDanceStore(s => s.setSelectedFootMarkerLabel);
  const [hoveredLabel, setHoveredLabel] = useState<number | null>(null);
  const controlsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedLabel === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (controlsRef.current?.contains(target)) return;
      if (target.closest('[data-location-marker-hitbox]')) return;
      setSelectedLabel(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [selectedLabel]);

  if (markers.length === 0) return null;

  return (
    <group>
      {markers.map((marker, idx) => {
        const isSelected = selectedLabel === marker.label;
        return (
          <group
            key={idx}
            position={marker.position}
          >
            {/* Screen-space circular hitbox — anchored between ring and label */}
            <Html position={[0, 0.2, 0]} style={{ pointerEvents: 'none' }}>
              <div
                data-location-marker-hitbox
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelectedLabel(marker.label);
                  setSelectedFootLabel(null);
                }}
                onMouseEnter={() => {
                  setHoveredLabel(marker.label);
                  const el = document.getElementById('stage-3d-container');
                  if (el) el.style.cursor = 'pointer';
                }}
                onMouseLeave={() => {
                  setHoveredLabel(null);
                  const el = document.getElementById('stage-3d-container');
                  if (el) el.style.cursor = '';
                }}
                style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  background: 'transparent',
                }}
              />
            </Html>

            {/* Visual: Target ring on floor */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} raycast={() => null}
              onPointerDown={(e) => {
                e.stopPropagation();
                setSelectedLabel(marker.label);
                setSelectedFootLabel(null);
              }}
            >
              <ringGeometry args={[0.3, 0.4, 32]} />
              <meshStandardMaterial
                color={(isSelected || hoveredLabel === marker.label) ? "#7dd3fc" : "#fb7185"}
                transparent
                opacity={(isSelected || hoveredLabel === marker.label) ? 0.8 : 0.4}
                emissive={(isSelected || hoveredLabel === marker.label) ? "#7dd3fc" : "#fb7185"}
                emissiveIntensity={(isSelected || hoveredLabel === marker.label) ? 1.0 : 0.5}
              />
            </mesh>
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]} raycast={() => null}>
              <circleGeometry args={[0.05, 16]} />
              <meshStandardMaterial
                color={(isSelected || hoveredLabel === marker.label) ? "#7dd3fc" : "#fb7185"}
                transparent
                opacity={(isSelected || hoveredLabel === marker.label) ? 1.0 : 0.6}
                emissive={(isSelected || hoveredLabel === marker.label) ? "#7dd3fc" : "#fb7185"}
                emissiveIntensity={(isSelected || hoveredLabel === marker.label) ? 1.5 : 0.8}
              />
            </mesh>

            <Html position={[0, 0.4, 0]} style={{ pointerEvents: 'none' }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                transform: 'translate(-50%, -100%)',
                pointerEvents: 'none'
              }}>
                <div style={{
                  background: isSelected ? '#7dd3fc' : (hoveredLabel === marker.label ? 'rgba(251, 113, 133, 1.0)' : 'rgba(251, 113, 133, 0.9)'),
                  color: '#0f172a',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  userSelect: 'none',
                  boxShadow: isSelected ? '0 0 15px rgba(125, 211, 252, 0.8)' : (hoveredLabel === marker.label ? '0 0 14px rgba(251, 113, 133, 0.9)' : '0 0 10px rgba(251, 113, 133, 0.5)'),
                  transform: hoveredLabel === marker.label && !isSelected ? 'scale(1.1)' : 'scale(1)',
                  pointerEvents: 'none',
                  transition: 'all 0.15s ease'
                }}>
                  L{marker.label}
                </div>
                {isSelected && (
                  <div ref={controlsRef} style={{
                    display: 'flex',
                    gap: '6px',
                    pointerEvents: 'auto',
                    background: 'rgba(15, 23, 42, 0.85)',
                    backdropFilter: 'blur(8px)',
                    padding: '3px 6px',
                    borderRadius: '14px',
                    border: '1px solid rgba(251, 113, 133, 0.3)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4), 0 0 6px rgba(251, 113, 133, 0.1)',
                    marginTop: '3px',
                    animation: 'scaleIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.255)'
                  }}>
                    <button
                      id="tutorial-snap-location-marker"
                      onClick={(e) => {
                        e.stopPropagation();
                        useDanceStore.getState().snapToLocationMarkerLabel(marker.label);
                      }}
                      style={{
                        background: 'rgba(14, 165, 233, 0.2)',
                        color: '#0ea5e9',
                        border: '1px solid rgba(14, 165, 233, 0.4)',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        transition: 'all 0.2s ease',
                      }}
                      title={t('stage.snap_to_location')}
                    >
                      <Target size={12} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLocationMarker(marker.label);
                      }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.15)',
                        color: '#f87171',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        transition: 'all 0.2s ease',
                      }}
                      title={t('stage.delete_location_marker')}
                    >
                      <Trash2 size={12} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLabel(null);
                      }}
                      style={{
                        background: 'rgba(100, 116, 139, 0.15)',
                        color: '#94a3b8',
                        border: '1px solid rgba(100, 116, 139, 0.3)',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        transition: 'all 0.2s ease',
                      }}
                      title={t('common.cancel', 'Dismiss')}
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                {isSelected && (
                  <div style={{
                    width: 0,
                    height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderTop: '6px solid rgba(251, 113, 133, 0.4)',
                  }} />
                )}
              </div>
            </Html>
          </group>
        );
      })}
    </group>
  );
};

const VerticalAttitudeIndicators: FC = () => {
  const { t } = useTranslation();
  const currentPose = useDanceStore(s => s.currentPose);
  const groundedElev = useDanceStore(s => s.groundedElevation);
  const elevation = currentPose.elevation || [0, 0, 0];
  const isGrounded = Math.abs(elevation[1] - groundedElev) < 0.01;
  const minY = 0.05 - groundedElev; // Relative Y of lowest point in world units

  // Calculate height in world units
  // Internal top is 17.5. World top is elevation[1] + (17.5 * 0.5).
  // Total mannequin height remains constant regardless of elevation.
  const mannequinHeight = (17.5 - (0.05 - calculateGroundedElevation(INITIAL_POSE)) * 2) * 0.5;
  // Actually, simpler: minY_standing = 0.05 - calculateGroundedElevation(INITIAL_POSE)
  // Height = (17.5 * 0.5) - minY_standing
  const diskRadius = mannequinHeight * 0.6;

  return (
    <group position={[elevation[0], 0, elevation[2]]}>
      {/* Vertical line from lowest point to floor */}
      <Line
        points={[
          [0, elevation[1] + minY, 0],
          [0, 0.05, 0] // Down to floor (world 0.05)
        ]}
        color={isGrounded ? "#38bdf8" : "#f59e0b"}
        lineWidth={1.5}
        transparent
        opacity={isGrounded ? 0.2 : 0.8}
        dashed={!isGrounded}
        dashScale={2}
        dashSize={0.2}
        dashOffset={0}
      />

      {/* Reference line above head */}
      <Line
        points={[
          [0, elevation[1] + (17.5 * 0.5), 0],
          [0, elevation[1] + (14 * 0.5), 0]
        ]}
        color={isGrounded ? "#38bdf8" : "#f59e0b"}
        lineWidth={1}
        transparent
        opacity={0.2}
      />

      {/* Base horizontal indicator circle (at lowest point) */}
      <group position={[0, elevation[1] + minY, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh>
          <circleGeometry args={[diskRadius, 64]} />
          <meshBasicMaterial color={isGrounded ? "#38bdf8" : "#f59e0b"} transparent opacity={0.05} side={THREE.DoubleSide} />
        </mesh>
        <mesh>
          <ringGeometry args={[diskRadius - 0.02, diskRadius + 0.02, 64]} />
          <meshBasicMaterial color={isGrounded ? "#38bdf8" : "#f59e0b"} transparent opacity={0.15} />
        </mesh>
      </group>

      {/* Floor point indicator */}
      <group position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh>
          <ringGeometry args={[0.04, 0.06, 32]} />
          <meshBasicMaterial color={isGrounded ? "#38bdf8" : "#f59e0b"} transparent opacity={0.5} />
        </mesh>
        <mesh>
          <circleGeometry args={[0.015, 16]} />
          <meshBasicMaterial color={isGrounded ? "#38bdf8" : "#f59e0b"} />
        </mesh>
        {!isGrounded && (
          <>
            <mesh>
              <ringGeometry args={[0.1, 0.11, 32]} />
              <meshBasicMaterial color="#f59e0b" transparent opacity={0.3} />
            </mesh>
            <Html position={[0, -0.4, 0]} transform rotation={[Math.PI / 2, 0, 0]}>
              <div style={{
                background: 'rgba(245, 158, 11, 0.9)',
                color: '#0f172a',
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '8px',
                fontWeight: '900',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
                boxShadow: '0 0 10px rgba(245, 158, 11, 0.5)',
                animation: 'pulse 1.5s infinite ease-in-out'
              }}>
                {t('stage.floating')}
              </div>
            </Html>
          </>
        )}
      </group>
    </group>
  );
};

export const CameraInfo: FC = () => {
  const { t } = useTranslation();
  const lastCameraState = useDanceStore(s => s.lastCameraState);
  const showCameraDebug = useDanceStore(s => s.showCameraDebug);
  const setShowCameraDebug = useDanceStore(s => s.setShowCameraDebug);

  if (!showCameraDebug || !lastCameraState) return null;

  const pos = lastCameraState.position;
  const target = lastCameraState.target;

  return (
    <div style={{
      position: 'relative',
      marginTop: '5px', // 5px flex gap + 5px margin = 10px gap below Attitude sidebar
      width: '136px',
      background: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(56, 189, 248, 0.3)',
      borderRadius: '8px',
      padding: '4px 6px',
      color: '#e2e8f0',
      fontSize: '0.62rem',
      fontFamily: 'monospace',
      boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
      pointerEvents: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      userSelect: 'text',
      WebkitUserSelect: 'text',
      MozUserSelect: 'text'
    }}>
      <div className="flex-between" style={{ marginBottom: '1px' }}>
        <div style={{ color: '#38bdf8', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.65rem' }}>
          <Camera size={10} />
          <span>Telemetry</span>
        </div>
        <button
          onClick={() => setShowCameraDebug(false)}
          style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0px' }}
        >
          <X size={11} />
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', fontSize: '0.6rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#94a3b8' }}>Pos:</span>
          <span>
            <span style={{ color: '#f87171' }}>X</span>{pos[0].toFixed(1)}
            <span style={{ color: '#4ade80', marginLeft: '3px' }}>Y</span>{pos[1].toFixed(1)}
            <span style={{ color: '#60a5fa', marginLeft: '3px' }}>Z</span>{pos[2].toFixed(1)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#94a3b8' }}>Tgt:</span>
          <span>
            <span style={{ color: '#f87171' }}>X</span>{target[0].toFixed(1)}
            <span style={{ color: '#4ade80', marginLeft: '3px' }}>Y</span>{target[1].toFixed(1)}
            <span style={{ color: '#60a5fa', marginLeft: '3px' }}>Z</span>{target[2].toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  );
};

export const Stage3D: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const controlsRef = useRef<any>(null);
  const [isAltPressed, setIsAltPressed] = useState(false);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const isPosing = useDanceStore((state: DanceState) => state.isPosing);
  const setIsPosing = useDanceStore((state: DanceState) => state.setIsPosing);
  const setActiveAxis = useDanceStore((state: DanceState) => state.setActiveAxis);
  const activeModule = useDanceStore((state: DanceState) => state.activeModule);
  const isFeedbackOpen = useDanceStore((state: DanceState) => state.isFeedbackOpen);

  const tutorialProgress = useStorageStore(state => state.tutorialProgress);
  const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
  const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
  const isOrientationsTutorial = currentStep?.id === 'stage-orientations';


  useEffect(() => {
    const keys: Record<string, boolean> = {};
    const handleDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      const isFeedbackOpen = useDanceStore.getState().isFeedbackOpen;
      const target = e.target as HTMLElement;
      if (isFeedbackOpen || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      const key = e.key.toLowerCase();
      if (e.key === 'Control') setIsCtrlPressed(true);
      if (e.key === 'Alt') setIsAltPressed(true);
      keys[key] = true;
      const isOrientationSelect = useDanceStore.getState().selectedJoint === 'orientation';
      const forbiddenForOrientation = isOrientationSelect && (key === 'x' || key === 'z');

      if (['x', 'y', 'z', 'a', 's', 'd', 'f'].includes(key) && !forbiddenForOrientation) {
        e.preventDefault();
        setIsPosing(true);
        setActiveAxis(
          key === 'a' ? 'A' :
            key === 's' ? 'S' :
              key === 'd' ? 'D' :
                key === 'f' ? 'F' :
                  key.toUpperCase() as 'X' | 'Y' | 'Z'
        );
      }
    }
    const handleUp = (e: KeyboardEvent) => {
      if (!e.key) return;
      const key = e.key.toLowerCase();
      if (e.key === 'Control') setIsCtrlPressed(false);
      if (e.key === 'Alt') setIsAltPressed(false);
      keys[key] = false;
      const isOrientationSelect = useDanceStore.getState().selectedJoint === 'orientation';

      const xHeld = keys['x'] && (!isOrientationSelect);
      const zHeld = keys['z'] && (!isOrientationSelect);
      const yHeld = keys['y'];
      const aHeld = keys['a'];
      const sHeld = keys['s'];
      const dHeld = keys['d'];
      const fHeld = keys['f'];

      if (!xHeld && !yHeld && !zHeld && !aHeld && !sHeld && !dHeld && !fHeld) {
        setIsPosing(false);
        setActiveAxis(null);
      } else {
        // Switch to whatever is still held (respecting forbidden keys)
        if (xHeld) setActiveAxis('X');
        else if (yHeld) setActiveAxis('Y');
        else if (zHeld) setActiveAxis('Z');
        else if (aHeld) setActiveAxis('A');
        else if (sHeld) setActiveAxis('S');
        else if (dHeld) setActiveAxis('D');
        else if (fHeld) setActiveAxis('F');
      }
    }
    const handleBlur = () => {
      setIsCtrlPressed(false);
      setIsAltPressed(false);
      setIsPosing(false);
      setActiveAxis(null);
    };
    window.addEventListener('keydown', handleDown);
    window.addEventListener('keyup', handleUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleDown);
      window.removeEventListener('keyup', handleUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [setIsPosing, setActiveAxis]);

  const openContextMenu = useDanceStore(s => s.openContextMenu);
  const isMirroring = useDanceStore(s => s.isMirroring);
  const setIsMirroring = useDanceStore(s => s.setIsMirroring);
  const showLabels = useDanceStore(s => s.showLabels);
  const setShowLabels = useDanceStore(s => s.setShowLabels);
  const showAxes = useDanceStore(s => s.showAxes);
  const setShowAxes = useDanceStore(s => s.setShowAxes);
  const addKeyframe = useDanceStore(s => s.addKeyframe);
  const resetPose = useDanceStore(s => s.resetPose);
  const resetOrientation = useDanceStore(s => s.resetOrientation);
  const mirrorCurrentPose = useDanceStore(s => s.mirrorCurrentPose);
  const resetCamera = useDanceStore(s => s.resetCamera);
  const addLocationMarker = useDanceStore(s => s.addLocationMarker);
  const addFootMarker = useDanceStore(s => s.addFootMarker);
  const clearAllMarkers = useDanceStore(s => s.clearAllMarkers);
  const clearFootMarkers = useDanceStore(s => s.clearFootMarkers);
  const clearLocationMarkers = useDanceStore(s => s.clearLocationMarkers);
  const setJointRotation = useDanceStore(s => s.setJointRotation);
  const actingSource = useDanceStore(s => s.actingSource);
  const lastCameraState = useDanceStore(s => s.lastCameraState);
  const openInputDialog = useDanceStore(s => s.openInputDialog);
  const confirmAction = useDanceStore(s => s.confirmAction);
  const savePoseToLib = useStorageStore(s => s.savePose);
  const selectedFolderId = useStorageStore(s => s.selectedPoseFolderId);
  const poseFolders = useStorageStore(s => s.folders);

  const orbitMouseButtons = useMemo(() => ({
    LEFT: isAltPressed ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN,
    MIDDLE: THREE.MOUSE.ROTATE,
    RIGHT: undefined
  }), [isAltPressed]);

  const handleContextMenu = (e: React.MouseEvent) => {
    // If we are hovering a joint, let the joint's own context menu handle it
    if (useDanceStore.getState().isHoveringJoint) return;

    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;

    const currentPoseState = useDanceStore.getState().currentPose;
    const elevationState = currentPoseState.elevation || [0, 0, 0];
    const groundElev = calculateGroundedElevation(currentPoseState);
    const isGrounded = Math.abs(elevationState[1] - groundElev) < 0.01;

    const items: MenuItem[] = isMobile ? [
      {
        label: t('stage.reset_camera', 'Reset Camera'),
        icon: Compass,
        action: () => resetCamera()
      },
      { isDivider: true },
      {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
            <span>{t('stage.show_labels')}</span>
            <div style={{
              background: showLabels ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: showLabels ? '#4ade80' : '#f87171',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              border: `1px solid ${showLabels ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              minWidth: '24px',
              textAlign: 'center'
            }}>
              {showLabels ? t('common.on') : t('common.off')}
            </div>
          </div>
        ),
        icon: Tag,
        action: () => setShowLabels(!showLabels)
      },
      {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
            <span>{t('stage.show_axes')}</span>
            <div style={{
              background: showAxes ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: showAxes ? '#4ade80' : '#f87171',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              border: `1px solid ${showAxes ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              minWidth: '24px',
              textAlign: 'center'
            }}>
              {showAxes ? t('common.on') : t('common.off')}
            </div>
          </div>
        ),
        icon: Axis3d,
        action: () => setShowAxes(!showAxes)
      }
    ] : [
      {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
            <span>{t('stage.mirror_mode')}</span>
            <div style={{
              background: isMirroring ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: isMirroring ? '#4ade80' : '#f87171',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              border: `1px solid ${isMirroring ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              minWidth: '24px',
              textAlign: 'center'
            }}>
              {isMirroring ? t('common.on') : t('common.off')}
            </div>
          </div>
        ),
        icon: Link,
        action: () => setIsMirroring(!isMirroring)
      },
      {
        label: t('stage.flip_pose'),
        icon: FlipHorizontal2,
        action: () => mirrorCurrentPose()
      },
      { isDivider: true },
      {
        label: t('stage.save_pose'),
        icon: PersonStanding,
        subItems: [
          {
            label: t('stage.to_frame'),
            icon: ArrowDown,
            action: () => addKeyframe(),
            disabled: isPosing || (actingSource.type !== 'timeline' && actingSource.type !== 'library')
          },
          {
            label: t('stage.to_library'),
            icon: ArrowUp,
            action: () => {
              const isLibrary = actingSource.type === 'library';
              
              const doSave = (id?: string, name?: string) => {
                const finalName = name || (isLibrary ? actingSource.name : 'Untitled');
                try {
                  const savedId = savePoseToLib({
                    id,
                    name: finalName,
                    folderId: selectedFolderId,
                    pose: { ...useDanceStore.getState().currentPose },
                    cameraState: lastCameraState || undefined
                  });

                  // Update editor state to link to the newly saved/updated pose
                  useDanceStore.getState().setActingSource({
                    type: 'library',
                    id: savedId,
                    name: finalName
                  });
                } catch (err) {
                  confirmAction({
                    title: t('library.save_failed'),
                    message: t('library.save_failed_message', { name: finalName }),
                    confirmLabel: t('common.retry'),
                    cancelLabel: t('common.cancel'),
                    onConfirm: () => doSave(id, name)
                  });
                }
              };

              const timestamp = new Date().toLocaleTimeString().replace(/:/g, '-');
              const defaultName = isLibrary ? actingSource.name : `Pose-${timestamp}`;

              if (isLibrary) {
                confirmAction({
                  title: t('stage.save_pose'),
                  message: t('library.overwrite_message', { name: actingSource.name }),
                  confirmLabel: t('library.overwrite'),
                  neutralLabel: t('library.save_as_new'),
                  cancelLabel: t('common.cancel'),
                  onConfirm: () => doSave(actingSource.id, actingSource.name),
                  onNeutral: () => {
                    const folder = poseFolders.find(f => f.id === selectedFolderId);
                    openInputDialog({
                      title: t('library.save_as_new_pose'),
                      defaultName: `${actingSource.name} (Copy)`,
                      submitLabel: t('common.save'),
                      folderName: folder?.name,
                      onSave: (name) => doSave(undefined, name)
                    });
                  }
                });
              } else {
                const folder = poseFolders.find(f => f.id === selectedFolderId);
                openInputDialog({
                  title: t('library.save_pose_to_library'),
                  defaultName,
                  submitLabel: t('common.save'),
                  folderName: folder?.name,
                  onSave: (name) => doSave(undefined, name)
                });
              }
            }
          }
        ]
      },
      {
        label: t('stage.reset'),
        icon: RotateCcw,
        subItems: [
          { label: t('stage.pose'), icon: PersonStanding, action: () => resetPose() },
          { label: t('stage.elevation'), icon: ChevronDown, action: () => setJointRotation('elevation', 0, calculateGroundedElevation(useDanceStore.getState().currentPose), 0) },
          { label: t('stage.orientation'), icon: Move, action: () => resetOrientation() },
          { label: t('stage.camera'), icon: Compass, action: () => resetCamera() },
          {
            label: t('stage.markers'),
            icon: Target,
            subItems: [
              { label: t('stage.delete_all'), icon: Trash2, action: () => clearAllMarkers() },
              { label: t('stage.foot_markers'), icon: Footprints, action: () => clearFootMarkers() },
              { label: t('stage.location_markers'), icon: MapPin, action: () => clearLocationMarkers() }
            ]
          }
        ]
      },
      {
        label: t('stage.mark'),
        icon: Target,
        subItems: [
          { label: t('stage.location'), icon: MapPin, action: () => addLocationMarker() },
          {
            label: t('stage.feet'),
            icon: Footprints,
            action: () => addFootMarker(),
            disabled: !isGrounded
          }
        ]
      },
      { isDivider: true },
      {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
            <span>{t('stage.show_labels')}</span>
            <div style={{
              background: showLabels ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: showLabels ? '#4ade80' : '#f87171',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              border: `1px solid ${showLabels ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              minWidth: '24px',
              textAlign: 'center'
            }}>
              {showLabels ? t('common.on') : t('common.off')}
            </div>
          </div>
        ),
        icon: Tag,
        action: () => setShowLabels(!showLabels)
      },
      {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
            <span>{t('stage.show_axes')}</span>
            <div style={{
              background: showAxes ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: showAxes ? '#4ade80' : '#f87171',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              border: `1px solid ${showAxes ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              minWidth: '24px',
              textAlign: 'center'
            }}>
              {showAxes ? t('common.on') : t('common.off')}
            </div>
          </div>
        ),
        icon: Axis3d,
        action: () => setShowAxes(!showAxes)
      }
    ];

    openContextMenu(x, y, items);
  };

  const isHoveringJoint = useDanceStore(s => s.isHoveringJoint);

  return (
    <div
      id="stage-3d-container"
      className="stage-container"
      onContextMenu={handleContextMenu}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: '#020617',
        cursor: isHoveringJoint ? 'pointer' : (isPosing ? 'crosshair' : 'grab')
      }}
    >
      <Canvas
        shadows={{ type: THREE.PCFShadowMap }}
        frameloop="demand"
        camera={{ position: [-0.0, 12.1, 18.0], fov: 40 }}
        gl={{ preserveDrawingBuffer: true }}
        onPointerMissed={() => {
          useDanceStore.getState().setSelectedJoint(null);
          useDanceStore.getState().setSelectedFootMarkerLabel(null);
          useDanceStore.getState().setSelectedLocationMarkerLabel(null);
        }}
        onContextMenu={handleContextMenu}
      >
        <color attach="background" args={['#0f172a']} />



        <MouseInteractionController />
        <CameraSync controlsRef={controlsRef} isOrientationsTutorial={isOrientationsTutorial} />

        {/* Lighting */}
        <ambientLight intensity={0.5} />
        <hemisphereLight intensity={0.5} groundColor="#1e1b4b" color="#ffffff" />
        <directionalLight
          position={[5, 10, 5]}
          intensity={1.4}
          castShadow
          shadow-mapSize={2048}
        />
        <spotLight
          position={[-5, 5, 5]}
          intensity={0.25}
          penumbra={1}
          color="#38bdf8"
        />

        <FootMarkers />
        <LocationMarkers />
        <FloorLocationPicker />
        <VerticalAttitudeIndicators />
        <TutorialShortcutTarget />

        {/* Environment and Stage */}
        <Suspense fallback={null}>
          <ErrorBoundary name="Environment">
            <Environment files="/textures/potsdamer_platz_1k.hdr" environmentIntensity={0.1} />
          </ErrorBoundary>
        </Suspense>

        {/* Core Stage Content - Now appears instantaneously */}
        <MannequinWrapper />

          {/* Floor Effects */}
          <DynamicContactShadows />
          {/* Shadow receiver — invisible plane so directional light casts onto the floor */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} receiveShadow>
            <planeGeometry args={[100, 100]} />
            <shadowMaterial transparent opacity={0.35} />
          </mesh>
          {/* Grid Floor */}
          <Grid
            position={[0, -0.01, 0]}
            args={[100, 100]}
            cellSize={1}
            cellThickness={0.8}
            cellColor="#312e81"
            sectionSize={5}
            sectionThickness={1.2}
            sectionColor="#38bdf8"
            fadeDistance={50}
            fadeStrength={1}
            infiniteGrid
          />
          {/* Audience Direction Arrow (Premium Glass / Static / Elevated) */}
          {activeModule === 'PoseDesigner' && (
            <>
              <group position={[0, 9.5, -4]} rotation={[Math.PI / 2, 0, 0]} scale={0.7}>
                <mesh geometry={AUDIENCE_GEO}>
                  <meshStandardMaterial
                    color="#38bdf8"
                    transparent
                    opacity={isOrientationsTutorial ? 0.8 : 0.3}
                    emissive="#38bdf8"
                    emissiveIntensity={isOrientationsTutorial ? 1.5 : 0.5}
                    metalness={0.9}
                    roughness={0.1}
                  />
                  {isOrientationsTutorial && (
                    <Html center portal={{ current: document.body }} pointerEvents="none" style={{ pointerEvents: 'none' }}>
                      <div id="tutorial-target-audience-arrow" style={{ width: '100px', height: '100px', pointerEvents: 'none' }} />
                    </Html>
                  )}
                </mesh>
              </group>

              <Audience />
            </>
          )}

        <OrbitControls
          ref={controlsRef}
          makeDefault
          target={[-0.0, 2.6, 0.7]}
          minPolarAngle={Math.PI / 4}
          maxPolarAngle={Math.PI / 2 + 0.1}
          minDistance={5}
          maxDistance={30}
          enabled={!isPosing}
          enableDamping={true}
          dampingFactor={0.2}
          screenSpacePanning={true}
          mouseButtons={orbitMouseButtons}
        />
      </Canvas>
    </div>
  );
};



